import React, { useState, useEffect } from 'react';
import { BrowserRouter, Routes, Route } from 'react-router-dom';
import { Toaster } from './components/ui/sonner';
import { Navbar } from './components/Navbar';
import { Footer } from './components/Footer';
import Home from './pages/Home';
import Services from './pages/Services';
import Gallery from './pages/Gallery';
import Shop from './pages/Shop';
import Cart from './pages/Cart';
import Checkout from './pages/Checkout';
import Contact from './pages/Contact';
import AdminLogin from './pages/admin/AdminLogin';
import AdminRegister from './pages/admin/AdminRegister';
import AdminDashboard from './pages/admin/AdminDashboard';
import AdminProducts from './pages/admin/AdminProducts';
import AdminProjects from './pages/admin/AdminProjects';
import AdminContacts from './pages/admin/AdminContacts';
import AdminOrders from './pages/admin/AdminOrders';
import AdminServices from './pages/admin/AdminServices';
import AdminRevisions from './pages/admin/AdminRevisions';
import AdminTrash from './pages/admin/AdminTrash';
import AdminUsers from './pages/admin/AdminUsers';
import AdminStudio from './pages/admin/AdminStudio';
import AdminQuotes from './pages/admin/AdminQuotes';
import AdminQuoteEditor from './pages/admin/AdminQuoteEditor';
import AdminVisualizer from './pages/admin/AdminVisualizer';
import AdminPreInvest from './pages/admin/AdminPreInvest';
import AdminLottoGen from './pages/admin/AdminLottoGen';
import PublicQuotePage from './pages/PublicQuotePage';
import PublicPredictions from './pages/PublicPredictions';
import ProjectDetail from './pages/ProjectDetail';
import OrderTracking from './pages/OrderTracking';
import ProductDetail from './pages/ProductDetail';
import './App.css';

function App() {
  const [cartItems, setCartItems] = useState(() => {
    try {
      const saved = localStorage.getItem('brainwave_cart');
      return saved ? JSON.parse(saved) : [];
    } catch {
      return [];
    }
  });
  const [isAuthenticated, setIsAuthenticated] = useState(false);

  // Check admin authentication on mount
  useEffect(() => {
    const token = localStorage.getItem('admin_token');
    setIsAuthenticated(!!token);
  }, []);

  // Save cart to localStorage whenever it changes
  useEffect(() => {
    localStorage.setItem('brainwave_cart', JSON.stringify(cartItems));
  }, [cartItems]);

  const addToCart = (product) => {
    setCartItems((prevItems) => {
      const existingItem = prevItems.find((item) => item.id === product.id);
      if (existingItem) {
        return prevItems.map((item) =>
          item.id === product.id
            ? { ...item, quantity: item.quantity + 1 }
            : item
        );
      }
      return [...prevItems, { ...product, quantity: 1 }];
    });
  };

  const updateQuantity = (productId, newQuantity) => {
    if (newQuantity < 1) return;
    setCartItems((prevItems) =>
      prevItems.map((item) =>
        item.id === productId ? { ...item, quantity: newQuantity } : item
      )
    );
  };

  const removeFromCart = (productId) => {
    setCartItems((prevItems) => prevItems.filter((item) => item.id !== productId));
  };

  const clearCart = () => {
    setCartItems([]);
  };

  const cartItemsCount = cartItems.reduce((total, item) => total + item.quantity, 0);

  return (
    <div className="App">
      <BrowserRouter>
        <Routes>
          {/* Public Routes */}
          <Route path="/" element={
            <>
              <Navbar cartItemsCount={cartItemsCount} />
              <Home />
              <Footer />
            </>
          } />
          <Route path="/services" element={
            <>
              <Navbar cartItemsCount={cartItemsCount} />
              <Services />
              <Footer />
            </>
          } />
          <Route path="/gallery" element={
            <>
              <Navbar cartItemsCount={cartItemsCount} />
              <Gallery />
              <Footer />
            </>
          } />
          <Route path="/gallery/:id" element={
            <>
              <Navbar cartItemsCount={cartItemsCount} />
              <ProjectDetail />
              <Footer />
            </>
          } />
          <Route path="/shop" element={
            <>
              <Navbar cartItemsCount={cartItemsCount} />
              <Shop addToCart={addToCart} />
              <Footer />
            </>
          } />
          <Route path="/shop/:id" element={
            <>
              <Navbar cartItemsCount={cartItemsCount} />
              <ProductDetail addToCart={addToCart} />
              <Footer />
            </>
          } />
          <Route path="/cart" element={
            <>
              <Navbar cartItemsCount={cartItemsCount} />
              <Cart 
                cartItems={cartItems}
                updateQuantity={updateQuantity}
                removeFromCart={removeFromCart}
              />
              <Footer />
            </>
          } />
          <Route path="/checkout" element={
            <>
              <Navbar cartItemsCount={cartItemsCount} />
              <Checkout 
                cartItems={cartItems}
                clearCart={clearCart}
              />
              <Footer />
            </>
          } />
          <Route path="/contact" element={
            <>
              <Navbar cartItemsCount={cartItemsCount} />
              <Contact />
              <Footer />
            </>
          } />
          <Route path="/track-order" element={
            <>
              <Navbar cartItemsCount={cartItemsCount} />
              <OrderTracking />
              <Footer />
            </>
          } />

          {/* Public quote share — no navbar/footer chrome, standalone page */}
          <Route path="/q/:token" element={<PublicQuotePage />} />
          <Route path="/predictions" element={<PublicPredictions />} />

          {/* Admin Routes */}
          <Route path="/admin/login" element={<AdminLogin setIsAuthenticated={setIsAuthenticated} />} />
          <Route path="/admin/register" element={<AdminRegister />} />
          <Route path="/admin/dashboard" element={<AdminDashboard />} />
          <Route path="/admin/products" element={<AdminProducts />} />
          <Route path="/admin/projects" element={<AdminProjects />} />
          <Route path="/admin/contacts" element={<AdminContacts />} />
          <Route path="/admin/orders" element={<AdminOrders />} />
          <Route path="/admin/services" element={<AdminServices />} />
          <Route path="/admin/revisions" element={<AdminRevisions />} />
          <Route path="/admin/trash" element={<AdminTrash />} />
          <Route path="/admin/users" element={<AdminUsers />} />
          <Route path="/admin/studio" element={<AdminStudio />} />
          <Route path="/admin/quotes" element={<AdminQuotes />} />
          <Route path="/admin/quotes/:id" element={<AdminQuoteEditor />} />
          <Route path="/admin/visualizer" element={<AdminVisualizer />} />
          <Route path="/admin/preinvest" element={<AdminPreInvest />} />
          <Route path="/admin/lotto-gen" element={<AdminLottoGen />} />
        </Routes>
        <Toaster />
      </BrowserRouter>
    </div>
  );
}

export default App;
